import React, { useEffect, useState } from 'react'
import {
  Select,
  Text,
  Label,
  XStack,
  View,
  useMedia,
  ScrollView,
  Dialog,
} from 'tamagui'
import { HeroIcon } from '@components/HeroIcon'
import { Modal } from './Modal'

interface IHeroSelectProps extends React.ComponentProps<typeof Select> {
  label?: string
  options: { name: string }[]
  showInvalidFeedback?: boolean
  placeholder?: string
}

export function HeroSelect({
  label,
  options,
  showInvalidFeedback = false,
  onValueChange,
  placeholder,
  ...props
}: IHeroSelectProps) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(props.value)
  const [positionTop, setPositionTop] = useState(0)
  const media = useMedia()

  const handleValueChange = (value: string) => {
    setValue(value)
    onValueChange && onValueChange(value)
    setOpen(false)
  }

  useEffect(() => {
    setValue((prev) => {
      if (prev !== props.value) {
        setOpen(false)
        return props.value
      }
      return prev
    })
  }, [value, props])

  return (
    <>
      {label && (
        <Label
          color="$text-primary"
          ff="$mulishRegular"
          fontSize="$font-size-xxs"
        >
          {label}
        </Label>
      )}
      {media.gtSm ? (
        <View gap={15} testID="desktop-select">
          <Select {...props} onValueChange={onValueChange}>
            <Select.Trigger
              width="100%"
              backgroundColor="$bg-input"
              borderWidth={1}
              borderColor="$border-strong"
              borderRadius={4}
              paddingVertical={8}
              paddingHorizontal={16}
              height={40}
              iconAfter={
                <HeroIcon
                  icon={['fal', 'chevron-down']}
                  size={16}
                  color="$icon-tertiary"
                />
              }
              onMouseEnter={(e) => {
                const position = e.nativeEvent.clientY
                setPositionTop(position)
              }}
            >
              <Select.Value
                placeholder={placeholder || ''}
                color="$text-secondary"
                numberOfLines={1}
              />
            </Select.Trigger>
            <Select.Content zIndex={200000}>
              <Select.Viewport
                maxWidth={150}
                style={{
                  top: positionTop,
                }}
                $gtSm={{ maxWidth: 'auto' }}
              >
                <Select.Group>
                  {options.map((option, i) => {
                    return (
                      <Select.Item
                        index={i}
                        key={option.name}
                        value={option.name.toLowerCase()}
                        hoverStyle={{ backgroundColor: '$accent-subtle' }}
                        backgroundColor={
                          props.value === option.name.toLocaleLowerCase()
                            ? '$bg-surface'
                            : '$bg-surface-raised'
                        }
                      >
                        <Select.ItemText
                          color="$text-secondary"
                          whiteSpace="break-spaces"
                          paddingVertical={4}
                        >
                          {option.name}
                        </Select.ItemText>
                      </Select.Item>
                    )
                  })}
                </Select.Group>
              </Select.Viewport>
            </Select.Content>
          </Select>
        </View>
      ) : (
        <>
          <XStack
            testID="mobile-select"
            width="100%"
            backgroundColor="$bg-input"
            borderWidth={1}
            borderColor="$border-strong"
            borderRadius={4}
            paddingVertical={8}
            paddingHorizontal={16}
            height={40}
            justifyContent="space-between"
            alignItems="center"
            onPress={() => setOpen(true)}
          >
            <Text
              color="$text-primary"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
              numberOfLines={1}
            >
              {props.value}
            </Text>
            <HeroIcon
              icon={['fal', 'chevron-down']}
              size={16}
              color="$icon-tertiary"
            />
            <Modal
              onClose={() => setOpen(false)}
              open={open}
              snapPoints={[80, 100]}
              backdropOpacity={0.9}
            >
              <Dialog.Title
                color="$text-primary"
                ff="$mulishBold"
                fontSize="$font-size-md"
                textAlign="center"
                marginTop={20}
              >
                {label}
              </Dialog.Title>
              <ScrollView paddingVertical={20} paddingHorizontal={14}>
                {options.map((option) => {
                  const isSelected = value === option.name
                  return (
                    <XStack
                      key={option.name}
                      alignItems="center"
                      marginBottom="$spacing-inset-nano"
                      borderWidth={1}
                      borderColor="$border-default"
                      borderRadius="$border-radius-sm"
                      overflow="hidden"
                      backgroundColor="$bg-surface"
                      onPress={() => handleValueChange(option.name)}
                    >
                      <View
                        alignSelf="stretch"
                        width={8}
                        backgroundColor={isSelected ? '$accent' : 'transparent'}
                      />
                      <View
                        width={20}
                        height={20}
                        borderRadius={10}
                        borderWidth={1}
                        marginLeft="$spacing-inset-xs"
                        alignItems="center"
                        justifyContent="center"
                        borderColor={
                          isSelected ? '$accent' : '$control-inactive'
                        }
                      >
                        {isSelected && (
                          <View
                            width={10}
                            height={10}
                            borderRadius={5}
                            backgroundColor="$accent"
                          />
                        )}
                      </View>
                      <Text
                        flex={1}
                        ff="$mulishRegular"
                        color="$text-primary"
                        fontSize="$font-size-xxs"
                        whiteSpace="break-spaces"
                        padding="$spacing-inset-xs"
                      >
                        {option.name}
                      </Text>
                    </XStack>
                  )
                })}
              </ScrollView>
            </Modal>
          </XStack>
        </>
      )}
      {showInvalidFeedback && (
        <XStack gap={5} alignItems="center" testID="select-invalid-feedback">
          <HeroIcon
            icon={['fal', 'exclamation-circle']}
            size={12}
            color="$danger-300"
          />
          <Text
            color="$danger-300"
            fontFamily="$mulishRegular"
            fontSize="$font-size-xxxs"
          >
            Este campo é obrigatório! Por favor, selecione uma opção.
          </Text>
        </XStack>
      )}
    </>
  )
}
